/**
 * A tiny, safe expression evaluator for computed variables.
 *
 * Deliberately NOT JS eval or new Function: projects are untrusted input, so the
 * grammar is a closed recursive-descent parser over a fixed operator set with a
 * whitelist of pure math helpers. No property access, no calls into host
 * objects, no assignment, no loops.
 *
 * Grammar (highest precedence last):
 *   expr    := ternary
 *   ternary := or ( '?' expr ':' expr )?
 *   or      := and ( '||' and )*
 *   and     := equality ( '&&' equality )*
 *   equality:= compare ( ('=='|'!=') compare )*
 *   compare := additive ( ('<'|'<='|'>'|'>=') additive )*
 *   additive:= multiplicative ( ('+'|'-') multiplicative )*
 *   mul     := unary ( ('*'|'/'|'%') unary )*
 *   unary   := ('-'|'!')? primary
 *   primary := number | string | ident | call | '(' expr ')'
 */

export type ExprValue = number | string | boolean | null;

export class ExpressionError extends Error {}

type TokenType = 'num' | 'str' | 'ident' | 'op' | 'eof';

interface Token {
  type: TokenType;
  value: string;
  pos: number;
}

const OPERATORS = [
  '===', '!==', '==', '!=', '<=', '>=', '&&', '||', '??',
  '+', '-', '*', '/', '%', '(', ')', ',', '?', ':', '<', '>', '!',
];

/** Pure, side-effect free functions callable from expressions. */
export const EXPR_FUNCTIONS: Record<string, (...args: ExprValue[]) => ExprValue> = {
  min: (...a) => Math.min(...a.map(toNumber)),
  max: (...a) => Math.max(...a.map(toNumber)),
  abs: (a) => Math.abs(toNumber(a)),
  round: (a, digits) => {
    const d = digits === undefined ? 0 : Math.max(0, Math.min(12, toNumber(digits)));
    const f = 10 ** d;
    return Math.round(toNumber(a) * f) / f;
  },
  floor: (a) => Math.floor(toNumber(a)),
  ceil: (a) => Math.ceil(toNumber(a)),
  sqrt: (a) => Math.sqrt(Math.max(0, toNumber(a))),
  pow: (a, b) => Math.pow(toNumber(a), toNumber(b)),
  clamp: (v, lo, hi) => Math.min(Math.max(toNumber(v), toNumber(lo)), toNumber(hi)),
  len: (a) => String(a ?? '').length,
  number: (a) => toNumber(a),
  string: (a) => String(a ?? ''),
  if: (c, t, f) => (truthy(c) ? t : f),
};

export function toNumber(v: ExprValue): number {
  if (typeof v === 'number') return v;
  if (typeof v === 'boolean') return v ? 1 : 0;
  if (v === null || v === undefined) return 0;
  const n = Number(String(v).trim());
  return Number.isFinite(n) ? n : 0;
}

export function truthy(v: ExprValue): boolean {
  if (typeof v === 'boolean') return v;
  if (typeof v === 'number') return v !== 0;
  if (v === null) return false;
  return String(v).length > 0 && String(v) !== 'false';
}

function tokenize(src: string): Token[] {
  const tokens: Token[] = [];
  let i = 0;
  while (i < src.length) {
    const ch = src[i];
    if (ch === ' ' || ch === '\t' || ch === '\n' || ch === '\r') {
      i += 1;
      continue;
    }
    if (ch >= '0' && ch <= '9') {
      let j = i;
      while (j < src.length && /[0-9._]/.test(src[j])) j += 1;
      const raw = src.slice(i, j).replace(/_/g, '');
      if (!/^\d+(\.\d+)?$/.test(raw)) throw new ExpressionError(`bad number "${raw}" at ${i}`);
      tokens.push({ type: 'num', value: raw, pos: i });
      i = j;
      continue;
    }
    if (ch === '"' || ch === "'") {
      let j = i + 1;
      let out = '';
      while (j < src.length && src[j] !== ch) {
        if (src[j] === '\\' && j + 1 < src.length) {
          out += src[j + 1];
          j += 2;
        } else {
          out += src[j];
          j += 1;
        }
      }
      if (j >= src.length) throw new ExpressionError(`unterminated string at ${i}`);
      tokens.push({ type: 'str', value: out, pos: i });
      i = j + 1;
      continue;
    }
    if (/[A-Za-z_$]/.test(ch)) {
      // No dots: variable names never contain them, and allowing them would
      // let "process.exit(1)" parse as a single identifier that only fails at
      // evaluation time. Rejecting it here keeps property access out of the
      // grammar entirely.
      let j = i;
      while (j < src.length && /[A-Za-z0-9_$]/.test(src[j])) j += 1;
      tokens.push({ type: 'ident', value: src.slice(i, j), pos: i });
      i = j;
      continue;
    }
    const op = OPERATORS.find((o) => src.startsWith(o, i));
    if (op) {
      tokens.push({ type: 'op', value: op, pos: i });
      i += op.length;
      continue;
    }
    throw new ExpressionError(`unexpected character "${ch}" at ${i}`);
  }
  tokens.push({ type: 'eof', value: '', pos: src.length });
  return tokens;
}

interface Node {
  run(scope: Record<string, ExprValue>, depth: number): ExprValue;
  /** Identifiers this node reads; used for dependency ordering. */
  deps(out: Set<string>): void;
}

const MAX_DEPTH = 64;

class Literal implements Node {
  constructor(private readonly value: ExprValue) {}
  run(): ExprValue {
    return this.value;
  }
  deps(): void {}
}

class Ident implements Node {
  constructor(private readonly name: string) {}
  run(scope: Record<string, ExprValue>): ExprValue {
    const v = scope[this.name];
    return v === undefined ? null : v;
  }
  deps(out: Set<string>): void {
    out.add(this.name);
  }
}

class Unary implements Node {
  constructor(private readonly op: string, private readonly operand: Node) {}
  run(scope: Record<string, ExprValue>, depth: number): ExprValue {
    const v = this.operand.run(scope, depth + 1);
    return this.op === '-' ? -toNumber(v) : !truthy(v);
  }
  deps(out: Set<string>): void {
    this.operand.deps(out);
  }
}

class Binary implements Node {
  constructor(
    private readonly op: string,
    private readonly left: Node,
    private readonly right: Node,
  ) {}

  run(scope: Record<string, ExprValue>, depth: number): ExprValue {
    if (depth > MAX_DEPTH) throw new ExpressionError('expression too deep');
    const op = this.op;
    if (op === '&&') {
      const l = this.left.run(scope, depth + 1);
      return truthy(l) ? this.right.run(scope, depth + 1) : l;
    }
    if (op === '||' || op === '??') {
      const l = this.left.run(scope, depth + 1);
      const take = op === '??' ? l === null : truthy(l);
      return take ? l : this.right.run(scope, depth + 1);
    }
    const a = this.left.run(scope, depth + 1);
    const b = this.right.run(scope, depth + 1);
    switch (op) {
      case '+':
        if (typeof a === 'string' || typeof b === 'string') return String(a ?? '') + String(b ?? '');
        return toNumber(a) + toNumber(b);
      case '-':
        return toNumber(a) - toNumber(b);
      case '*':
        return toNumber(a) * toNumber(b);
      case '/': {
        const d = toNumber(b);
        return d === 0 ? 0 : toNumber(a) / d;
      }
      case '%': {
        const d = toNumber(b);
        return d === 0 ? 0 : toNumber(a) % d;
      }
      case '==':
      case '===':
        return looseEquals(a, b);
      case '!=':
      case '!==':
        return !looseEquals(a, b);
      case '<':
        return toNumber(a) < toNumber(b);
      case '<=':
        return toNumber(a) <= toNumber(b);
      case '>':
        return toNumber(a) > toNumber(b);
      case '>=':
        return toNumber(a) >= toNumber(b);
      default:
        throw new ExpressionError(`unknown operator ${op}`);
    }
  }

  deps(out: Set<string>): void {
    this.left.deps(out);
    this.right.deps(out);
  }
}

class Ternary implements Node {
  constructor(
    private readonly cond: Node,
    private readonly then: Node,
    private readonly other: Node,
  ) {}
  run(scope: Record<string, ExprValue>, depth: number): ExprValue {
    return truthy(this.cond.run(scope, depth + 1))
      ? this.then.run(scope, depth + 1)
      : this.other.run(scope, depth + 1);
  }
  deps(out: Set<string>): void {
    this.cond.deps(out);
    this.then.deps(out);
    this.other.deps(out);
  }
}

class Call implements Node {
  constructor(private readonly name: string, private readonly args: Node[]) {}
  run(scope: Record<string, ExprValue>, depth: number): ExprValue {
    const fn = Object.prototype.hasOwnProperty.call(EXPR_FUNCTIONS, this.name)
      ? EXPR_FUNCTIONS[this.name]
      : undefined;
    if (!fn) throw new ExpressionError(`unknown function "${this.name}"`);
    return fn(...this.args.map((a) => a.run(scope, depth + 1)));
  }
  deps(out: Set<string>): void {
    this.args.forEach((a) => a.deps(out));
  }
}

function looseEquals(a: ExprValue, b: ExprValue): boolean {
  if (typeof a === 'string' || typeof b === 'string') return String(a ?? '') === String(b ?? '');
  return toNumber(a) === toNumber(b);
}

class Parser {
  private pos = 0;
  constructor(private readonly tokens: Token[]) {}

  private peek(): Token {
    return this.tokens[this.pos];
  }

  private next(): Token {
    return this.tokens[this.pos++];
  }

  private accept(value: string): boolean {
    const t = this.peek();
    if (t.type === 'op' && t.value === value) {
      this.pos += 1;
      return true;
    }
    return false;
  }

  private expect(value: string): void {
    if (!this.accept(value)) {
      throw new ExpressionError(`expected "${value}" at ${this.peek().pos}`);
    }
  }

  parse(): Node {
    const node = this.ternary();
    if (this.peek().type !== 'eof') {
      throw new ExpressionError(`unexpected token "${this.peek().value}" at ${this.peek().pos}`);
    }
    return node;
  }

  private ternary(): Node {
    const cond = this.binary(0);
    if (this.accept('?')) {
      const then = this.ternary();
      this.expect(':');
      return new Ternary(cond, then, this.ternary());
    }
    return cond;
  }

  private static readonly LEVELS = [
    ['||', '??'],
    ['&&'],
    ['==', '!=', '===', '!=='],
    ['<', '<=', '>', '>='],
    ['+', '-'],
    ['*', '/', '%'],
  ];

  private binary(level: number): Node {
    if (level >= Parser.LEVELS.length) return this.unary();
    let left = this.binary(level + 1);
    for (;;) {
      const t = this.peek();
      if (t.type !== 'op' || !Parser.LEVELS[level].includes(t.value)) return left;
      this.next();
      left = new Binary(t.value, left, this.binary(level + 1));
    }
  }

  private unary(): Node {
    const t = this.peek();
    if (t.type === 'op' && (t.value === '-' || t.value === '!')) {
      this.next();
      return new Unary(t.value, this.unary());
    }
    return this.primary();
  }

  private primary(): Node {
    const t = this.next();
    if (t.type === 'num') return new Literal(Number(t.value));
    if (t.type === 'str') return new Literal(t.value);
    if (t.type === 'ident') {
      if (t.value === 'true') return new Literal(true);
      if (t.value === 'false') return new Literal(false);
      if (t.value === 'null') return new Literal(null);
      if (this.accept('(')) {
        const args: Node[] = [];
        if (!this.accept(')')) {
          do {
            args.push(this.ternary());
          } while (this.accept(','));
          this.expect(')');
        }
        return new Call(t.value, args);
      }
      return new Ident(t.value);
    }
    if (t.type === 'op' && t.value === '(') {
      const inner = this.ternary();
      this.expect(')');
      return inner;
    }
    throw new ExpressionError(`unexpected "${t.value || 'end of input'}" at ${t.pos}`);
  }
}

export interface CompiledExpression {
  readonly source: string;
  readonly dependencies: readonly string[];
  evaluate(scope: Record<string, ExprValue>): ExprValue;
}

const cache = new Map<string, CompiledExpression>();

export function compileExpression(source: string): CompiledExpression {
  const cached = cache.get(source);
  if (cached) return cached;
  const ast = new Parser(tokenize(source)).parse();
  const deps = new Set<string>();
  ast.deps(deps);
  const compiled: CompiledExpression = {
    source,
    dependencies: [...deps],
    evaluate: (scope) => ast.run(scope, 0),
  };
  if (cache.size > 512) cache.clear();
  cache.set(source, compiled);
  return compiled;
}

/** Convenience wrapper: never throws, returns `fallback` on any error. */
export function evaluateExpression(
  source: string,
  scope: Record<string, ExprValue>,
  fallback: ExprValue = null,
): ExprValue {
  try {
    return compileExpression(source).evaluate(scope);
  } catch {
    return fallback;
  }
}
